<script
	lang="ts"
	module
>
	export type Type = 'ASSEMBLY' | 'PART' | 'VEHICLE'

	//end of module section
</script>

<script lang="ts">
	import type { Manufacturer, InventoryType } from 'utility/search-fields'

	import { startsWithVowel } from 'utility/language-tools'
	import Autocomplete from '@isoftdata/svelte-autocomplete'
	import { loadPartManufacturersForInventoryType, loadVehicleManufacturers } from 'utility/search-fields'
	import { untrack, type ComponentProps } from 'svelte'
	import type { i18n } from 'types/common'
	import { getContext } from 'svelte'
	const { t: translate } = getContext<i18n>('i18next')

	interface Props extends Omit<
		ComponentProps<typeof Autocomplete<Manufacturer | null>>,
		'value' | 'options' | 'getLabel' | 'type' | 'placeholder'
	> {
		initialValue?: number // For initial value binding
		manufacturer?: Manufacturer | null
		inventoryType?: InventoryType | null | undefined
		disabled?: boolean
		type: Type // No default value here, require the consumer to pass the type of manufacturer.
	}

	let {
		//
		initialValue,
		manufacturer = $bindable(null),
		inventoryType = undefined,
		disabled = false,
		type,
		...rest
	}: Props = $props()

	const typeLabelMap = new Map<Type, string>([
		['ASSEMBLY', translate('partSearch.assemblyManufacturer', 'Assembly Manufacturer')],
		['PART', translate('common:partManufacturer', 'Part Manufacturer')],
		['VEHICLE', translate('common:vehicleMake', 'Vehicle Make')],
	])

	const typePlaceholderMap = new Map<Type, string>([
		['ASSEMBLY', translate('search.selectAnAssemblyManufacturer', 'Select an Assembly Manufacturer')],
		['PART', translate('search.selectAPartManufacturer', 'Select a Part Manufacturer')],
		['VEHICLE', translate('search.selectAVehicleMake', 'Select a Vehicle Make')],
	])

	let manufacturerList: Promise<Manufacturer[]> | Manufacturer[] = $state([])

	$effect(() => {
		type
		inventoryType
		untrack(() => loadManufacturers(type, inventoryType))
	})
	let computedPlaceholder = $derived(computePlaceholder(type, inventoryType))
	let label = $derived(typeLabelMap.get(type) ?? translate('common:manufacturer', 'Manufacturer'))

	async function loadManufacturers(type: Type, inventoryType: InventoryType | null | undefined) {
		if (inventoryType) {
			manufacturerList = loadPartManufacturersForInventoryType(
				type === 'ASSEMBLY' ? (inventoryType?.setId ?? inventoryType?.id) : inventoryType?.id,
			)
			manufacturerList.then(loadedManufacturers => {
				manufacturerList = loadedManufacturers
				if (initialValue) {
					// If initialValue is provided, we try to find the manufacturer
					manufacturer = loadedManufacturers.find(manufacturer => manufacturer.id === initialValue) ?? null
				}
				// If we have a selected manufacturer but after loading the new list, it's not in the list, then we need to the clear the selection
				else if (
					manufacturer &&
					!loadedManufacturers.find(loadedManufacturer => loadedManufacturer.id === manufacturer?.id)
				) {
					manufacturer = null
				}
			})
		} else if (type === 'VEHICLE') {
			manufacturerList = loadVehicleManufacturers()
			if (initialValue) {
				manufacturerList.then(loadedManufacturers => {
					manufacturer = loadedManufacturers.find(manufacturer => manufacturer.id === initialValue) ?? null
				})
			} else {
				manufacturer = null
			}
		} else {
			manufacturerList = []
			manufacturer = null
		}
	}

	function computePlaceholder(type: Type, inventoryType: InventoryType | null | undefined): string {
		if (type !== 'VEHICLE' && !inventoryType) {
			return translate('search.selectAnInventoryTypeFirst', 'Select an inventory type first')
		}

		return typePlaceholderMap.get(type) ?? translate('search.selectAManufacturer', 'Select a Manufacturer')
	}
</script>

<Autocomplete
	{...rest}
	{label}
	getLabel={option => option?.name ?? ''}
	options={manufacturerList}
	bind:value={manufacturer}
	emptyValue={null}
	placeholder={computedPlaceholder}
	disabled={disabled || (type !== 'VEHICLE' && !inventoryType)}
></Autocomplete>
